import {DisplayMatrix, DisplayTypeEnum, RenderV2_DisplayItem} from '../render-v2-d';

/**
 * Gaps for the boundaries between CSS grid columns and rows.
 * columnGaps[i] is the space after the column whose one-based start is i + 1.
 * rowGaps[i] is the space after the row whose one-based start is i + 1.
 * There is no entry for the last column or the last row.
 */
export type MatrixLayout = {
    columnGaps: number[];
    rowGaps: number[];
};

const GAP_MATCH_EPSILON = 1e-6;

/**
 * Cells on one shared boundary declared different gaps.
 * -1 is not a declaration. Only Doc cells are read.
 */
export class InconsistentMatrixGapError extends Error {
    readonly direction: 'column' | 'row';
    readonly boundaryIndex: number;
    readonly values: readonly number[];

    constructor(direction: 'column' | 'row', boundaryIndex: number, values: number[]) {
        const axis = direction === 'column' ? 'gapRight' : 'gapBelow';

        super(`Inconsistent ${axis} on shared ${direction} boundary ${boundaryIndex}: ${values.join(', ')} inches`);
        this.name = 'InconsistentMatrixGapError';
        this.direction = direction;
        this.boundaryIndex = boundaryIndex;
        this.values = values;
    }
}

/** One-based grid line, matching displayItemGridLines: falsy starts fall back to 1. */
export const gridStart = (value: number | undefined): number => value || 1;

/**
 * One gap per real grid boundary, shared by frame size and placement.
 *
 * Boundaries are keyed by each item's `columnStart` / `rowStart`, not by displayMatrix array indices.
 * Last-column gapRight and last-row gapBelow are ignored, including nonzero values.
 * -1 means the cell does not declare a gap. A boundary with no declaration is 0.
 * Only Doc cells count. Insignia and lino values are ignored.
 * When more than one Doc on a boundary declares a gap, the values must agree or this throws.
 */
export const resolveMatrixLayout = (matrix: DisplayMatrix): MatrixLayout => {
    const items = matrix.flatMap(row => row ?? []).filter((item): item is RenderV2_DisplayItem => !!item);
    const maxColumn = items.reduce((max, item) => Math.max(max, gridStart(item.columnStart)), 1);
    const maxRow = items.reduce((max, item) => Math.max(max, gridStart(item.rowStart)), 1);
    const columnGaps: number[] = [];
    const rowGaps: number[] = [];

    for (let columnStart = 1; columnStart < maxColumn; columnStart++) {
        columnGaps.push(resolveBoundary('column', columnStart - 1, declaredGapsOnColumn(items, columnStart)));
    }

    for (let rowStart = 1; rowStart < maxRow; rowStart++) {
        rowGaps.push(resolveBoundary('row', rowStart - 1, declaredGapsOnRow(items, rowStart)));
    }

    return {columnGaps, rowGaps};
};

const declaredGapsOnColumn = (items: RenderV2_DisplayItem[], columnStart: number): number[] => {
    const values: number[] = [];

    items.forEach(item => {
        if (gridStart(item.columnStart) !== columnStart) return;

        const declared = declaredGap(item, 'gapRight');

        if (declared !== null) values.push(declared);
    });

    return values;
};

const declaredGapsOnRow = (items: RenderV2_DisplayItem[], rowStart: number): number[] => {
    const values: number[] = [];

    items.forEach(item => {
        if (gridStart(item.rowStart) !== rowStart) return;

        const declared = declaredGap(item, 'gapBelow');

        if (declared !== null) values.push(declared);
    });

    return values;
};

const declaredGap = (item: RenderV2_DisplayItem, prop: 'gapRight' | 'gapBelow'): number | null => {
    if (item.displayType !== DisplayTypeEnum.Doc || item[prop] === -1) return null;

    return item[prop];
};

const resolveBoundary = (direction: 'column' | 'row', boundaryIndex: number, values: number[]): number => {
    if (values.length === 0) return 0;

    const first = values[0];
    const conflicts = values.some(value => Math.abs(value - first) > GAP_MATCH_EPSILON);

    if (conflicts) throw new InconsistentMatrixGapError(direction, boundaryIndex, values);

    return first;
};
